// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable/Clickable";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngQuantityBreakPrice from "@rugby/App/Components/CustomQuantityBreakPrice/XngQuantityBreakPrice";
import ProductAddToListLink from "@rugby/Overrides/Components/ProductAddToListLink";
import ProductListActions from "@rugby/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@rugby/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@rugby/Overrides/Widgets/ProductList/ProductListProductInformation";
import customProductGridCardCustomStyle from "@rugby/Overrides/Widgets/ProductList/styles/ProductListProductGridCard.style";

import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & HasProduct;
// </XNG-Changes>

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // <XNG-Changes>
    priceAndAvailabilityContainer?: any;
    actionsContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    compareCheckbox?: InjectableCss;
    // </XNG-Changes>
}

// <XNG-Info> we don't use it
export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

// <XNG-Changes>
const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

const styles = mergeToNew(productGridCardStyles, customProductGridCardCustomStyle);
// </XNG-Changes>

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    settingsCollection,
    productInfo,
}) => {
    const productContext = {
        product,
        productInfo,
    };

    const renderProductBrand = (
        <>
            {showBrand && (
                <StyledWrapper {...styles.brands.wrapper} data-test-selector="productListProductCard_brands">
                    {product.brand && (
                        <Clickable
                            {...styles.brands.linkWrappingImage}
                            href={product.brand?.detailPagePath}
                            data-test-selector="brandLink"
                        >
                            <LazyImage
                                {...styles.brands.image}
                                src={product.brand.logoSmallImagePath}
                                altText={product.brand.logoImageAltText || product.brand.name}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
            )}
        </>
    );

    const renderProductImage = (
        <>
            {showImage && (
                <StyledWrapper
                    {...styles.productImage.container}
                    data-test-selector="productListProductCard_productImage"
                >
                    <ProductListProductImage
                        showImage={showImage}
                        showCompare={false}
                        extendedStyles={styles.productImage.productListProductImage}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                    />
                </StyledWrapper>
            )}
        </>
    );

    const renderPriceAndAvailabilityContainer = (
        <StyledWrapper
            {...styles.priceAndAvailabilityContainer.wrapper}
            data-test-selector="productListProductCard_priceAndAvailability"
        >
            <ProductListActions
                showPrice={showPrice}
                showAvailability={false}
                showAddToList={false}
                showActions={false}
                extendedStyles={styles.priceAndAvailabilityContainer.price}
            />
            {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                <ProductContextAvailability extendedStyles={styles.priceAndAvailabilityContainer.availability} />
            )}
        </StyledWrapper>
    );

    const renderQtyAndAddToCartContainer = (
        <ProductListActions
            showActions={true}
            showAvailability={false}
            showPrice={false}
            showAddToList={false}
            addToCartIconOnly={false}
            showUnitOfMeasure={false}
            extendedStyles={styles.qtyAndAddToCartContainer}
        />
    );

    const renderCompareAndAddToListContainer = (
        <StyledWrapper {...styles.compareAndAddToCartContainer.wrapper}>
            <StyledWrapper>
                <ProductListProductImage
                    showImage={false}
                    showCompare={showCompare && !product.isVariantParent}
                    extendedStyles={styles.compareAndAddToCartContainer}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                />
            </StyledWrapper>

            <StyledWrapper>
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${product.id}`}
                        extendedStyles={styles.compareAndAddToCartContainer.addToListLink}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            {renderProductBrand}
            {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
            {renderProductImage}
            <StyledWrapper {...styles.productInfoContainer} data-test-selector="productListProductCard_infContainer">
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    isGridView={true}
                    showAvailability={false}
                    showBrand={false}
                    showMinPack={true}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                />
                <XngQuantityBreakPrice product={productContext} />
                <StyledWrapper {...styles.actionsContainer}>
                    {renderPriceAndAvailabilityContainer}
                    <StyledWrapper {...styles.actionsContainer} className="qtyAndAddToCartSection">
                        {renderQtyAndAddToCartContainer}
                        {renderCompareAndAddToListContainer}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductGridCard));
